import React, {useCallback} from "react";
import {Theme} from "@core-ui/react-stitch";
import {isEmpty} from "lodash";
import {dropdownCSS} from "./styles/dropdown";
import {DropdownComponents} from "./components";
import {CommonDropdownProps, CommonProps, Actions} from "./types";
import {onBlur, controlOnKeyDown, menuOnKeyDown} from "./handlers";
import {
  getOptionAriaLabel,
  getOptionLabel,
  optionKeyGenerator,
} from "./builtins";

const defaultProps = {
  menuIsOpen: false,
  highlightedIndex: 0,
  optionKeyGenerator,
  getOptionAriaLabel,
  getOptionLabel,
};

export type DropdownBaseProps = CommonDropdownProps &
  CommonProps &
  Actions & {
    components: DropdownComponents;
    controlRef: React.RefObject<HTMLButtonElement>;
    selectName: string;
    menuRef: React.RefObject<HTMLUListElement>;
  } & typeof defaultProps;

const DropdownBase = ({
  ariaDescribedByText,
  components,
  bemBuilder,
  onFocus,
  className,
  crossBrand,
  controlRef,
  disabled,
  getStyles,
  label,
  hasError,
  inverse,
  focus,
  getOptionAriaLabel,
  getOptionLabel,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  placeholder,
  value,
  menuRef,
  highlightedIndex,
  selectName,
  optionKeyGenerator,
  options = [],
  onBlur: onBlurProp,
  onChange,
  onOptionHighlighted,
  noOptionsMessage,
  reset,
  ariaLabelledBy,
  controlId,
  enabledFeatures,
}: DropdownBaseProps): JSX.Element => {
  const {Control, Menu, NoOptionsMessage, Option} = components;

  const commonProps = {
    getStyles,
    bemBuilder,
    crossBrand,
    disabled,
    focus,
    hasError,
    highlightedIndex,
    inverse,
    menuIsOpen,
    options,
    enabledFeatures,
  };

  const handleBlur = useCallback(
    (e) => onBlur({onBlur: onBlurProp, reset})(e),
    [onBlurProp, reset]
  );

  const handleControlOnKeyDown = useCallback(
    (e) => controlOnKeyDown({menuIsOpen, onMenuOpen})(e),
    [menuIsOpen, onMenuOpen]
  );

  const handleMenuOnKeyDown = useCallback(
    (e) =>
      menuOnKeyDown({
        menuIsOpen,
        onChange,
        highlightedIndex,
        options,
        onOptionHighlighted,
        reset,
        menuRef,
      })(e),
    [
      menuIsOpen,
      onChange,
      highlightedIndex,
      options,
      onOptionHighlighted,
      reset,
      menuRef,
    ]
  );
  return (
    <div
      className={className}
      css={(theme: Theme) =>
        getStyles("Container", dropdownCSS({theme, crossBrand}), {
          theme,
          ...commonProps,
        })
      }
      onBlur={handleBlur}
      onFocus={onFocus}
    >
      <Control
        ref={controlRef}
        ariaDescribedByText={ariaDescribedByText}
        ariaLabelledBy={ariaLabelledBy}
        getOptionAriaLabel={getOptionAriaLabel}
        getOptionLabel={getOptionLabel}
        id={controlId}
        label={label}
        onClick={!menuIsOpen ? onMenuOpen : onMenuClose}
        onKeyDown={handleControlOnKeyDown}
        placeholder={placeholder}
        value={value}
        {...commonProps}
      />
      <Menu ref={menuRef} onKeyDown={handleMenuOnKeyDown} {...commonProps}>
        {menuIsOpen &&
          (!isEmpty(options) ? (
            options.map((option, i) => (
              <Option
                {...commonProps}
                key={optionKeyGenerator(option, i)}
                highlighted={highlightedIndex === i}
                id={`dropdown-option${i}`}
                index={i}
                inverse={inverse}
                onClick={onChange}
                onMouseMove={() =>
                  i !== highlightedIndex && onOptionHighlighted(i)
                }
                option={option}
                selected={`${option}` === `${value}`}
              >
                {getOptionLabel(option)}
              </Option>
            ))
          ) : (
            <NoOptionsMessage {...commonProps}>
              {noOptionsMessage}
            </NoOptionsMessage>
          ))}
      </Menu>
      <select
        aria-hidden
        hidden
        name={selectName}
        onChange={() => {}}
        value={value}
      >
        {!isEmpty(value) &&
          !isEmpty(options) &&
          options.map((option, i) => (
            <option
              key={optionKeyGenerator(`${option}-hidden-select`, i)}
              value={option}
            >
              {option}
            </option>
          ))}
      </select>
    </div>
  );
};

DropdownBase.defaultProps = defaultProps;

export default DropdownBase;
